import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [orderForm, setOrderForm] = useState({
    userId: '',
    platform: '',
    examDate: '',
    examTime: '',
    price: '',
    currency: 'USD',
    paymentMethod: 'Crypto',
    paymentLink: '',
    txid: '',
    adminNotes: ''
  });

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchOrders();
    if (isAdmin) fetchUsers();
  }, [isLoggedIn, navigate, isAdmin]);

  // 🆕 FUNCIÓN: Formatear fecha a EST
  const formatEST = (dateString, timeString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return `${dateStr} ${timeString || ''} EST`;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isAdmin ? '/api/orders' : '/api/orders/my';
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/tickets/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // 🆕 FUNCIÓN: Eliminar orden
  const deleteOrder = async (orderId) => {
    if (!confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchOrders();
        setSelectedOrder(null);
        alert('Order deleted successfully');
      } else {
        alert('Error deleting order');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderForm)
      });

      if (res.ok) {
        setShowCreateModal(false);
        setOrderForm({
          userId: '',
          platform: '',
          examDate: '',
          examTime: '',
          price: '',
          currency: 'USD',
          paymentMethod: 'Crypto',
          paymentLink: '',
          txid: '',
          adminNotes: ''
        });
        fetchOrders();
        alert('Order created successfully!');
      } else {
        const data = await res.json();
        alert(data.message || 'Error creating order');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const updateOrder = async (orderId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        fetchOrders();
        setSelectedOrder(null);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-500/20 text-yellow-400',
      'confirmed': 'bg-blue-500/20 text-blue-400',
      'in-progress': 'bg-purple-500/20 text-purple-400',
      'completed': 'bg-green-500/20 text-green-400',
      'cancelled': 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-500/20 text-yellow-400',
      'paid': 'bg-green-500/20 text-green-400',
      'failed': 'bg-red-500/20 text-red-400',
      'refunded': 'bg-gray-500/20 text-gray-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  // Vista de USUARIO
  if (!isAdmin) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-container py-8">
        <div className="container">
          <h1 className="section-title mb-6">My Orders</h1>
          
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="card text-center py-12">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-text-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
              <p className="text-text-secondary mb-6">
                You don't have any active orders. Contact support to create one.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/dashboard')}
              >
                Go to Support
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <motion.div 
                  key={order._id}
                  className="card cursor-pointer hover:border-primary"
                  onClick={() => setSelectedOrder(order)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-xs text-text-gray mb-1">Order #{order.orderNumber}</p>
                      <h3 className="font-semibold text-lg">{order.platform}</h3>
                      <p className="text-sm text-text-secondary">
                        {formatEST(order.examDate, order.examTime)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-3 border-t border-border-color">
                    <div>
                      <p className="text-2xl font-bold text-white">${order.price}</p>
                      <p className="text-xs text-text-gray">{order.paymentMethod}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${getPaymentStatusColor(order.paymentStatus)}`}>
                      {order.paymentStatus}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Modal detalle orden usuario */}
          {selectedOrder && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Order Details</h2>
                  <button 
                    className="text-text-gray hover:text-white"
                    onClick={() => setSelectedOrder(null)}
                  >
                    ✕
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-dark-bg/50 rounded-lg">
                    <p className="text-xs text-text-gray mb-1">Order Number</p>
                    <p className="font-mono text-lg">{selectedOrder.orderNumber}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-text-gray mb-1">Platform</p>
                      <p className="font-semibold">{selectedOrder.platform}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-gray mb-1">Price</p>
                      <p className="font-semibold text-xl">${selectedOrder.price}</p>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <p className="text-xs text-text-gray mb-1">Exam Date & Time (EST)</p>
                    <p className="font-semibold">{formatEST(selectedOrder.examDate, selectedOrder.examTime)}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-text-gray mb-1">Payment Method</p>
                    <p className="font-semibold">{selectedOrder.paymentMethod}</p>
                  </div>
                  
                  {selectedOrder.paymentLink && (
                    <div>
                      <p className="text-xs text-text-gray mb-1">Payment Link</p>
                      <a 
                        href={selectedOrder.paymentLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {selectedOrder.paymentLink}
                      </a>
                    </div>
                  )}
                  
                  {selectedOrder.txid && (
                    <div>
                      <p className="text-xs text-text-gray mb-1">Transaction ID</p>
                      <p className="font-mono text-sm break-all">{selectedOrder.txid}</p>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-4">
                    <span className={`flex-1 text-center py-2 rounded ${getStatusColor(selectedOrder.status)}`}>
                      Status: {selectedOrder.status}
                    </span>
                    <span className={`flex-1 text-center py-2 rounded ${getPaymentStatusColor(selectedOrder.paymentStatus)}`}>
                      Payment: {selectedOrder.paymentStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Vista de ADMIN
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-container py-8">
      <div className="container">
        <div className="flex justify-between items-center mb-6">
          <h1 className="section-title mb-0">Orders Management</h1>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Order
          </button>
        </div>

        {/* Filtros */}
        <div className="flex gap-4 mb-6">
          <input
            type="text"
            placeholder="Search orders..."
            className="form-input flex-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select 
            className="form-input w-40"
            onChange={(e) => fetchOrders(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Lista de órdenes */}
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="card text-center py-8">No orders found.</div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <motion.div 
                key={order._id}
                className="card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-primary">{order.orderNumber}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getPaymentStatusColor(order.paymentStatus)}`}>
                        {order.paymentStatus}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-text-gray text-xs">Customer</p>
                        <p className="font-medium">{order.userName}</p>
                        <p className="text-text-gray text-xs">{order.userEmail}</p>
                      </div>
                      <div>
                        <p className="text-text-gray text-xs">Platform</p>
                        <p className="font-medium">{order.platform}</p>
                      </div>
                      <div>
                        <p className="text-text-gray text-xs">Exam Date (EST)</p>
                        <p>{formatEST(order.examDate, order.examTime)}</p>
                      </div>
                      <div>
                        <p className="text-text-gray text-xs">Price</p>
                        <p className="text-xl font-bold text-white">${order.price}</p>
                        <p className="text-text-gray text-xs">{order.paymentMethod}</p>
                      </div>
                    </div>
                    
                    {order.adminNotes && (
                      <div className="mt-3 p-2 bg-dark-bg/50 rounded">
                        <p className="text-xs text-text-gray">Notes: {order.adminNotes}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => setSelectedOrder(order)}
                    >
                      Edit
                    </button>
                    {/* 🆕 BOTÓN ELIMINAR */}
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteOrder(order._id)}
                    >
                      Delete
                    </button>
                    {order.ticketId && (
                      <button 
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate(`/dashboard?ticket=${order.ticketId}`)}
                      >
                        View Ticket
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Crear Orden - CON INDICADOR EST */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card max-w-2xl w-full">
            <h2 className="text-2xl font-semibold mb-6">Create New Order</h2>
            
            <form onSubmit={createOrder} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-text-secondary mb-2">Customer</label>
                  <select 
                    className="form-input w-full"
                    value={orderForm.userId}
                    onChange={(e) => setOrderForm({...orderForm, userId: e.target.value})}
                    required
                  >
                    <option value="">Select user...</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>
                        {u.username} ({u.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Platform</label>
                  <select 
                    className="form-input w-full"
                    value={orderForm.platform}
                    onChange={(e) => setOrderForm({...orderForm, platform: e.target.value})}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Bluebook">Bluebook</option>
                    <option value="Respondus">Respondus</option>
                    <option value="ProctorU">ProctorU</option>
                    <option value="Honorlock">Honorlock</option>
                    <option value="ExamSoft">ExamSoft</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Payment Method</label>
                  <select 
                    className="form-input w-full"
                    value={orderForm.paymentMethod}
                    onChange={(e) => setOrderForm({...orderForm, paymentMethod: e.target.value})}
                  >
                    <option value="Crypto">Crypto</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Exam Date</label>
                  <input 
                    type="date"
                    className="form-input w-full"
                    value={orderForm.examDate}
                    onChange={(e) => setOrderForm({...orderForm, examDate: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Exam Time (EST)</label>
                  <input 
                    type="time"
                    className="form-input w-full"
                    value={orderForm.examTime}
                    onChange={(e) => setOrderForm({...orderForm, examTime: e.target.value})}
                    required
                  />
                  <p className="text-xs text-text-gray mt-1">All times are Eastern Standard Time (EST)</p>
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Price</label>
                  <input 
                    type="number"
                    step="0.01"
                    className="form-input w-full"
                    value={orderForm.price}
                    onChange={(e) => setOrderForm({...orderForm, price: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Currency</label>
                  <select 
                    className="form-input w-full"
                    value={orderForm.currency}
                    onChange={(e) => setOrderForm({...orderForm, currency: e.target.value})}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="BTC">BTC</option>
                  </select>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm text-text-secondary mb-2">Payment Link</label>
                  <input 
                    type="url"
                    className="form-input w-full"
                    placeholder="https://..."
                    value={orderForm.paymentLink}
                    onChange={(e) => setOrderForm({...orderForm, paymentLink: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm text-text-secondary mb-2">Transaction ID (if paid)</label>
                  <input 
                    type="text"
                    className="form-input w-full"
                    value={orderForm.txid}
                    onChange={(e) => setOrderForm({...orderForm, txid: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm text-text-secondary mb-2">Admin Notes</label>
                  <textarea 
                    className="form-input w-full"
                    rows="3"
                    value={orderForm.adminNotes}
                    onChange={(e) => setOrderForm({...orderForm, adminNotes: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex gap-2 justify-end pt-4">
                <button 
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Orden - CON BOTÓN ELIMINAR */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-lg w-full">
            <h2 className="text-xl font-semibold mb-4">Update Order</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Status</label>
                <select 
                  className="form-input w-full"
                  value={selectedOrder.status}
                  onChange={(e) => updateOrder(selectedOrder._id, { status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">Payment Status</label>
                <select 
                  className="form-input w-full"
                  value={selectedOrder.paymentStatus}
                  onChange={(e) => updateOrder(selectedOrder._id, { paymentStatus: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">Transaction ID</label>
                <input 
                  type="text"
                  className="form-input w-full"
                  defaultValue={selectedOrder.txid}
                  onBlur={(e) => updateOrder(selectedOrder._id, { txid: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">Payment Link</label>
                <input 
                  type="url"
                  className="form-input w-full"
                  defaultValue={selectedOrder.paymentLink}
                  onBlur={(e) => updateOrder(selectedOrder._id, { paymentLink: e.target.value })}
                />
              </div>
              
              <div className="flex gap-2 justify-end pt-4 border-t border-border-color">
                <button 
                  className="btn btn-outline"
                  onClick={() => setSelectedOrder(null)}
                >
                  Close
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => deleteOrder(selectedOrder._id)}
                >
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Orders;